import React from "react";
import dayjs from "dayjs";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Radio,
  Button,
  Typography,
  Alert,
  Row,
  Col,
} from "antd";
import axios from "axios";
import type { FormSchema, FormField, Condition } from "../../Forms/sow.schema";

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

function evaluateCondition(cond: Condition, values: any) {
  const v = values?.[cond.field];

  if ("equals" in cond) return v === cond.equals;
  if ("notEquals" in cond) return v !== cond.notEquals;
  if ("in" in cond) return cond.in.includes(v);
  if ("truthy" in cond) return !!v && String(v).trim() !== "";
  return true;
}

function shouldShow(field: FormField, values: any) {
  if (!field.showWhen?.length) return true;
  return field.showWhen.every((c) => evaluateCondition(c, values));
}

function isRequired(field: FormField, values: any) {
  if (field.required) return true;
  if (!field.requireWhen?.length) return false;
  return field.requireWhen.every((c) => evaluateCondition(c, values));
}

type Props = {
  schema: FormSchema;
};

type LegalDocInsertPayload = {
  document_request_id: number;
  request_created: string; // ISO ok if your toMySqlDateTime handles it
  status: string | null;

  opportunity: string | null;
  opp_name_lookup: string | null;

  client_name: string | null;
  document_type: string | null;

  baa_needed: boolean | number | null;
  account_manager: string | null;
  comments: string | null;

  reason_no_draft_required: string | null;
  recruit: string | null;

  draft_created: boolean | number;
  finance_reviewed: boolean | number;
  am_reviewed: boolean | number;
  signed_by_client: boolean | number;

  link_to_draft_sow: string | null;
  signing_partner: string | null;
  link_to_existing_msa: string | null;
  link_to_existing_nda: string | null;

  draft_completed_date: string | null; // ISO ok
  reason_no_finance_review_req: string | null;

  // Optional: keep the raw form data for future template generation
  form_data?: any;
};

export default function DynamicLegalRequestForm({ schema }: Props) {
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [submitting, setSubmitting] = React.useState(false);

  const normalizeOptionalString = (v: any) => {
    if (v === undefined || v === null) return null;
    const s = String(v).trim();
    return s.length ? s : null;
  };

  const normalizeDate = (v: any) => {
    // AntD DatePicker returns dayjs object by default
    if (!v) return null;
    const d = dayjs(v);
    return d.isValid() ? d.format("YYYY-MM-DD") : null;
  };

  const toNullTrim = (v: any) => {
    if (v === undefined || v === null) return null;
    const s = String(v).trim();
    return s.length ? s : null;
  };

  const generateRequestId = () => Number(`${Date.now()}`);

  function buildLegalDocInsertPayload(values: any): LegalDocInsertPayload {
    const nowIso = new Date().toISOString();

    return {
      document_request_id: generateRequestId(),
      request_created: nowIso,
      status: "New",

      // You have "Opportunity" on the form; use it here.
      opportunity: toNullTrim(values.opportunity),
      opp_name_lookup: null, // if you have an opp lookup later, set it then

      client_name: toNullTrim(values.clientName),
      document_type: toNullTrim(values.documentType), // "SOW" | "NDA" | "Other"

      // You don’t collect BAA on the SOW form right now
      baa_needed: 0,

      account_manager: toNullTrim(values.accountManagerRequester),
      comments: toNullTrim(values.comments),

      // workflow / ops fields you aren’t collecting yet
      reason_no_draft_required: null,
      recruit: null,

      // defaults for boolean workflow flags
      draft_created: 0,
      finance_reviewed: 0,
      am_reviewed: 0,
      signed_by_client: 0,

      // links not collected in the form (yet)
      link_to_draft_sow: null,
      signing_partner: null,
      link_to_existing_msa: null,
      link_to_existing_nda: null,

      draft_completed_date: null,
      reason_no_finance_review_req: null,

      // Optional: store everything else for doc generation later
      form_data: {
        requestType: "SOW",
        sowStartDate: values.sowStartDate
          ? dayjs(values.sowStartDate).format("YYYY-MM-DD")
          : null,
        newSowEndDate: values.newSowEndDate
          ? dayjs(values.newSowEndDate).format("YYYY-MM-DD")
          : null,
        msaSignatureDate: toNullTrim(values.msaSignatureDate),
        sowType: toNullTrim(values.sowType),
        workLocation: toNullTrim(values.workLocation),
        travelIncludedInRate: toNullTrim(values.travelIncludedInRate),
        travelBudget: toNullTrim(values.travelBudget),
        travelBudgetType: toNullTrim(values.travelBudgetType),
        hasTotalAllocationAmount: toNullTrim(values.hasTotalAllocationAmount),
        dateMustSendBy: values.dateMustSendBy
          ? dayjs(values.dateMustSendBy).format("YYYY-MM-DD")
          : null,
        hdmPointOfContact: toNullTrim(values.hdmPointOfContact),
        projectName: toNullTrim(values.projectName),
        projectDescription: toNullTrim(values.projectDescription),
        servicesToBePerformed: toNullTrim(values.servicesToBePerformed),
        clientSigningAuthorityName: toNullTrim(
          values.clientSigningAuthorityName
        ),
        clientSigningAuthorityTitle: toNullTrim(
          values.clientSigningAuthorityTitle
        ),
        hdmSigner: toNullTrim(values.hdmSigner),
        hasCustomSowTemplate: toNullTrim(values.hasCustomSowTemplate),
      },
    };
  }

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const values = await form.validateFields();

      const payload = buildLegalDocInsertPayload(values);

      const res = await axios.post("/submit-legal-doc-request", payload);

      const requestId =
        res?.data?.document_request_id ?? payload.document_request_id;

      form.resetFields();
      form.setFieldsValue({ documentType: "SOW" });
    } catch (err: any) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  async function onFinish(data: any) {
    const payload = {
      requestType: schema.requestType,
      submittedAt: new Date().toISOString(),
      data,
    };

    await axios.post("/api/legal-doc-requests", payload);
  }

  function renderField(field: FormField) {
    const requiredNow = isRequired(field, values);
    if (!shouldShow(field, values)) return null;

    const rules: any[] = requiredNow
      ? [{ required: true, message: `${field.label} is required` }]
      : [];

    let inputNode: React.ReactNode;

    switch (field.type) {
      case "textarea":
        inputNode = <TextArea rows={5} />;
        break;

      case "select":
        inputNode = (
          <Select placeholder='Select…'>
            {field.options?.map((o) => (
              <Select.Option key={o.value} value={o.value}>
                {o.label}
              </Select.Option>
            ))}
          </Select>
        );
        break;

      case "radio":
        inputNode = (
          <Radio.Group>
            {field.options?.map((o) => (
              <Radio key={o.value} value={o.value}>
                {o.label}
              </Radio>
            ))}
          </Radio.Group>
        );
        break;

      case "date":
        inputNode = <DatePicker style={{ width: "100%" }} />;
        break;

      case "currency":
        inputNode = <Input prefix='$' />;
        rules.push({
          validator: (_: any, val: any) => {
            if (!val) return Promise.resolve();
            const cleaned = String(val).replace(/[$,]/g, "");
            return /^\d+(\.\d{1,2})?$/.test(cleaned)
              ? Promise.resolve()
              : Promise.reject("Enter a valid amount");
          },
        });
        break;

      default:
        inputNode = <Input />;
    }

    return (
      <Col span={12} key={field.name}>
        <Form.Item
          name={field.name}
          label={field.label}
          rules={rules}
          tooltip={field.helpText}
        >
          {inputNode}
        </Form.Item>
      </Col>
    );
  }

  return (
    <>
      <Title level={3}>{schema.title}</Title>

      {schema.description && (
        <Paragraph style={{ whiteSpace: "pre-wrap" }}>
          {schema.description}
        </Paragraph>
      )}

      {["NDA", "Other"].includes(values?.documentType) && (
        <Alert
          type='warning'
          showIcon
          style={{ marginBottom: 16 }}
          message='Slack Notification Required'
          description={
            <>
              If you selected <Text strong>NDA</Text> or{" "}
              <Text strong>Other</Text>, tag <Text code>@clientservices</Text>{" "}
              in Slack.
            </>
          }
        />
      )}

      <Form
        layout='vertical'
        form={form}
        onFinish={onFinish}
        initialValues={{
          documentType: schema.requestType,
        }}
      >
        <Row gutter={24}>{schema.fields.map(renderField)}</Row>

        <Form.Item>
          <Button type='primary' loading={submitting} onClick={onSubmit}>
            Submit Request
          </Button>
        </Form.Item>
      </Form>
    </>
  );
}
